import { fetchAuthSession } from 'aws-amplify/auth'
import { type Environment, getEnvironment } from '../utils/getEnvironment'

// Environment-specific GraphQL endpoints
const appStageToMergedGraphqlUri: Record<Environment, string> = {
  local: 'https://graph.dev.shipveho.com/graphql',
  development: 'https://graph.dev.shipveho.com/graphql',
  staging: 'https://graph.staging.shipveho.com/graphql',
  production: 'https://graph.shipveho.com/graphql',
}

function getGraphqlUri(): string {
  const environment = getEnvironment()
  const graphqlUri = import.meta.env.VITE_GRAPHQL_URI
  return graphqlUri || appStageToMergedGraphqlUri[environment] || appStageToMergedGraphqlUri.local
}

export interface GraphQLRequest<TVariables = Record<string, unknown>> {
  query: string
  variables?: TVariables
}

export interface GraphQLResponse<TData = unknown> {
  data?: TData
  errors?: Array<{ message: string; extensions?: unknown }>
}

export async function graphqlRequest<TData = unknown, TVariables = Record<string, unknown>>(
  query: GraphQLRequest<TVariables>['query'],
  variables: GraphQLRequest<TVariables>['variables']
): Promise<TData> {
  try {
    const session = await fetchAuthSession()
    const authToken = session.tokens?.idToken?.toString() ?? ''

    const uri = getGraphqlUri()

    const response = await fetch(uri, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result: GraphQLResponse<TData> = await response.json()

    // Handle GraphQL errors
    if (result.errors && result.errors.length > 0) {
      const errorMessage = result.errors.map(e => e.message).join(', ')
      console.error('GraphQL errors:', result.errors)
      throw new Error(`GraphQL Error: ${errorMessage}`)
    }

    if (!result.data) {
      throw new Error('No data returned from GraphQL request')
    }

    return result.data as TData
  } catch (error) {
    console.error('GraphQL request failed:', error)
    throw error
  }
}

export const graphqlRequestWrapper = <TData, TVariables>(
  query: string,
  variables?: TVariables
): (() => Promise<TData>) => {
  return async () => {
    return graphqlRequest<TData, TVariables>(query, variables)
  }
}

export { appStageToMergedGraphqlUri, getGraphqlUri }
